<!--
业务变更说明：
- 新增初始化流程，根据checkout-flow.md文档实现
- 实现从URL参数中获取订单信息或通过API获取订单信息
- 根据订单状态进行页面跳转：失败状态跳转失败页面，Shopify订单跳转新收银台，无支付国家跳转国家选择页面

Business Change Description:
- Added initialization process according to checkout-flow.md document
- Implemented getting order information from URL parameters or through API
- Page navigation based on order status: failed status redirects to failure page, Shopify order redirects to new checkout, no payment country redirects to country selection page
-->
<template>
  <div :style="{ '--brand-color': app.brandInfo?.color || '#503df6' }">
    <!-- 使用分割布局组件,并添加全屏加载效果 -->
    <split-layout>
      <!-- 左侧内容区域 -->
      <template #left>
        <!-- 订单信息组件 -->
        <OrderInfo :order="order" v-if="!isMobile" :isFormatAmount="isFormatAmount"/>
      </template>
      <!-- 右侧内容区域 -->
      <template #right>
        <div class="right-container">
          <!-- 吸顶 -->
          <div v-if="order">
            <div class="sticky-top">
              <OnCountdownTimer style="margin-bottom: 24px;" :createTime="order.createTime"
                :effectiveMinutes="order.effectiveMinutes" />
              <OnErrorMsgPanel ref="errorPanel" style="margin-bottom: 24px;" :visible="retryInfo.visible"
                :errorText="retryInfo.retryAdvice" @onClose="retryInfo.visible = false" />
            </div>
            <OrderInfo :order="order" v-if="isMobile"/>
          </div>
          <PaymentForm
            class="right-container-payment-form"
            ref="paymentFormRef"
            @updateStatus="setCheckoutStatus"
            @updateOrder="setOrder"
          ></PaymentForm>
        </div>

      </template>
    </split-layout>
    <!-- 成功状态 ,TODO billingAddress直接取order数据行不行,redirectUrl取order.data.redirectUrl是否可以-->
    <!-- 注，redirectUrl以支付结果拿到的为准，如果支付结果没有，则取order.data.redirectUrl -->
    <Modal :modelValue="currentStatus === CHECKOUT_STATUS.SUCCESS"
      :title="this.$t('message.result.success')"
      :description="modalMessage || this.$t('message.result.successPop')"
      :status="'success'"
      :buttonText="this.$t('message.checkout.returnMerchant')"
      :redirectUrl="redirectUrl || (order?.returnUrl || (order?.data && order?.data.redirectUrl))"
      :billingAddress="order && !order.hideBillingInformation ? order.billingAddress : ''"
    />
    <!-- 失败状态 -->
    <Modal :modelValue="currentStatus === CHECKOUT_STATUS.FAILED"
      :title="this.$t('message.result.failed')"
      :description="modalMessage ? `${this.$t('message.result.reason')}: ${modalMessage}` : ''"
      :status="'error'"
      :buttonText="this.$t('message.checkout.returnMerchant')"
      :redirectUrl="redirectUrl || (order?.returnUrl || (order?.data && order?.data.redirectUrl))"
    />
    <!-- Loading -->
    <LoadingArrow v-if="currentStatus === CHECKOUT_STATUS.LOADING" />
    <!-- 提交中 -->
    <LoadingArrow
      v-if="currentStatus === CHECKOUT_STATUS.SUBMITTING || currentStatus === CHECKOUT_STATUS.PENDING"
      :text="this.$t('message.result.processing')"
    />
    <!-- 错误 -->
    <ErrorPage v-if="currentStatus === CHECKOUT_STATUS.ERROR" />
  </div>
</template>

<script>
// 导入订单信息组件
import { mapGetters } from 'vuex';

import OnCountdownTimer from '@/componentsV2/busi/countdown-timer/index.vue';
import ErrorPage from '@/componentsV2/busi/Error/Error.vue';
import OnErrorMsgPanel from '@/componentsV2/busi/errorMsg-panel/index.vue';
import LoadingArrow from '@/componentsV2/busi/Loading/LoadingArrow.vue';
import Modal from '@/componentsV2/busi/Modal/Modal.vue';
import OrderInfo from '@/componentsV2/busi/OrderInfo/OrderInfo.vue';
import SplitLayout from '@/componentsV2/busi/SplitLayout.vue';

import { getOrderStatus } from './bus/initCheckout';
import { initFraudPrevention } from './bus/initFraudPrevention';
import { CHECKOUT_STATUS } from './config';
import { dotsCommasCountries } from '@/utils/tools/format';
import PaymentForm from './PaymentForm.vue';

// 导入分割布局组件
// 导入初始化结账流程函数
// 导入防欺诈初始化函数
export default {
  // 组件名称
  name: 'CheckoutV3',
  // 注册子组件
  components: {
    SplitLayout,
    OrderInfo,
    Modal,
    LoadingArrow,
    ErrorPage,
    PaymentForm,
    OnErrorMsgPanel,
    OnCountdownTimer,
  },
  // 组件数据
  data() {
    return {
      dotsCommasCountries,
      CHECKOUT_STATUS,
      // 当前状态
      currentStatus: CHECKOUT_STATUS.Filling,
      // 全屏加载状态
      // fullscreenLoading: true,
      // 是否为移动端，依据window.innerWidth<=991
      isMobile: window.innerWidth <= 991,
      // 订单信息对象
      // order: null,
      // 从URL获取订单key
      key: this.$route.query.key ? this.$route.query.key : this.$route.params.id,
      // 重试信息
      retryInfo: {
        visible: false,
        retry: undefined,
        retryAdvice: undefined,
      },
      modalMessage: '',
      redirectUrl: '',
      order: null,
    };
  },
  computed: {
    ...mapGetters(['app']),
    // 是否要格式化金额
    isFormatAmount() {
      return dotsCommasCountries.includes(this.order?.paymentCountry);
    },

  },
  // 组件创建时的生命周期钩子
  created() {
    // 设置浏览器标题
    document.title = this.$t('message.title');
    // 移动端判断及监听
    this.handleResize = () => {
      this.isMobile = window.innerWidth <= 991;
    };
    window.addEventListener('resize', this.handleResize);
    // this.initCheckout();
  },
  // 组件创建前的生命周期钩子
  beforeCreate() {
    // 初始化防欺诈功能
    initFraudPrevention(this.$route.query.key ? this.$route.query.key : this.$route.params.id);
  },
  // 组件销毁前的生命周期钩子
  beforeDestroy() {
    // 移除resize监听，防止内存泄漏
    window.removeEventListener('resize', this.handleResize);
  },
  // 组件方法
  methods: {
    /**
     * 修改结账状态
     * @param {string} status - 状态值
     * @param {string} message - 错误信息
     */
    setCheckoutStatus(status, message, others) {
      if (Object.values(CHECKOUT_STATUS).includes(status)) {
        this.currentStatus = status;
        this.modalMessage = message;
        this.redirectUrl = others?.redirectUrl;
        // 如果是pending状态，则调用轮询订单状态.
        if (status === CHECKOUT_STATUS.PENDING) {
          getOrderStatus({ key: this.key }).catch((error) => {
            this.setCheckoutStatus(error.status, error.message, error.others);
          });
        }
      } else {
        this.currentStatus = CHECKOUT_STATUS.ERROR;
        console.error('Invalid checkout status:', status);
      }
    },

    setOrder(order, isSkipRetry = false) {
      this.order = order;
      if (order.retry === '1') {
        this.setCheckoutStatus(CHECKOUT_STATUS.FILLING, '', order);
      }
      if (isSkipRetry) {
        return;
      }
      // 更新重试信息
      this.retryInfo.retry = order.retry;
      this.retryInfo.retryAdvice = order.retryAdvice;
      this.retryInfo.visible = order.retry === '1';
    },
    /**
     * 订单结束后跳转到失败页
     */
    // handleTimeEnd() {
    //   this.setCheckoutStatus(CHECKOUT_STATUS.FAILED, this.$t('message.error.paymentLinkExpired'), {
    //     redirectUrl: this.order.redirectUrl || undefined,
    //   });
    // },
  },
};
</script>

<style lang="scss" scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #fff;
  width: 100%;
}

.right-container {
  width: 100%;
  max-width: 406px;
}

.right-container-payment-form {
  width: 100%;
}
</style>
